import { SYSTEM_ENTITY_LABELS, SystemEntity } from '@erp/shared-types';

/**
 * Pure formatting for the generic anchor-summary fallback cell, kept free of
 * React imports so it stays trivially unit-testable (mirrors
 * taskQueueCellFormat.ts).
 */

/** Domain prefixes kept uppercase when prettifying a SystemEntity name. */
const ACRONYM_WORDS = new Set(['AP', 'AR', 'IM', 'SD', 'EDI', 'PO', 'OMS']);

/**
 * Own-property narrowing: a bare index would also resolve inherited keys such
 * as `toString`/`constructor` to non-string garbage instead of falling back.
 */
function isKnownSystemEntity(
  entityType: string,
): entityType is keyof typeof SYSTEM_ENTITY_LABELS {
  return Object.prototype.hasOwnProperty.call(SYSTEM_ENTITY_LABELS, entityType);
}

/**
 * Human label for an anchor entity type: the canonical
 * `SYSTEM_ENTITY_LABELS` entry when the value is a known `SystemEntity`,
 * otherwise a prettified fallback (e.g. `AP_INBOUND_EMAIL` →
 * "AP Inbound Email") so stale/unknown values still render.
 */
export function formatAnchorEntityTypeLabel(entityType: string): string {
  if (isKnownSystemEntity(entityType)) {
    return SYSTEM_ENTITY_LABELS[entityType];
  }
  return entityType
    .split('_')
    .filter(Boolean)
    .map((word) =>
      ACRONYM_WORDS.has(word)
        ? word
        : word.charAt(0) + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

/** Compact id fragment for the fallback cell (first uuid segment). */
export function shortAnchorEntityId(entityId: string): string {
  const firstSegment = entityId.split('-')[0] ?? entityId;
  return firstSegment.length < entityId.length
    ? `${firstSegment}…`
    : firstSegment;
}

function displayText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  return null;
}

/**
 * Badge text for an anchor from its batch-loaded label fields (in priority
 * order): the first non-empty value is the label, the next one a muted
 * secondary. Null when none loaded (the cell falls back to type + short id).
 */
export function anchorSummaryLabels(
  anchor: Record<string, unknown> | null | undefined,
  labelFields: readonly string[],
): { primary: string; secondary: string | null } | null {
  const texts = labelFields
    .map((field) => displayText(anchor?.[field]))
    .filter((text): text is string => text !== null);
  const [primary, secondary] = texts;
  return primary ? { primary, secondary: secondary ?? null } : null;
}

/**
 * Short record-kind prefix for anchors whose label is a bare number, so a
 * Famous order number reads "SO 128001" / "INV 128001" as on AR Tasks.
 */
const ANCHOR_LABEL_PREFIX: Partial<Record<SystemEntity, string>> = {
  [SystemEntity.AR_SALES_ORDER]: 'SO',
  [SystemEntity.AR_INVOICE]: 'INV',
};

export function withAnchorLabelPrefix(entityType: SystemEntity, label: string) {
  const prefix = ANCHOR_LABEL_PREFIX[entityType];
  return prefix && /^\d+$/.test(label) ? `${prefix} ${label}` : label;
}
